<template>
  <div class="brochure-zoom-share">
    <BaseIcon
      icon="first-page"
      class="interactive size-18"
      :class="{ 'light-gray': !hasPrev }"
      @click="hasPrev ? $emit('first') : null"
    />
    <BaseIcon
      icon="baseline-arrow-back-ios"
      class="interactive size-12"
      :class="{ 'disabled light-gray': !hasPrev }"
      @click="hasPrev ? $emit('prev') : null"
    />
    <div class="zoom-slider">
      <template v-if="showZoom">
        <BaseIcon icon="remove" class="interactive size-16" @click="zoomOut" />
        <input
          v-model.number="zoomLevel"
          type="range"
          min="1"
          max="3"
          step="0.01"
          class="zoom-input"
        />
        <BaseIcon icon="add" class="interactive size-16" @click="zoomIn" />
      </template>
    </div>
    <BaseIcon
      icon="baseline-arrow-forward-ios"
      class="interactive size-12"
      :class="{ 'light-gray': !hasNext }"
      @click="hasNext ? $emit('next') : null"
    />
    <BaseIcon
      icon="last-page"
      class="interactive size-18"
      :class="{ 'light-gray': !hasNext }"
      @click="hasNext ? $emit('last') : null"
    />
    <ShareButton
      v-if="brochureTitle"
      class="button-extra-small hide-small"
      :item="{ title: brochureTitle }"
    />
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  zoomLevel: number;
  brochureTitle?: string;
  hasPrev: boolean;
  hasNext: boolean;
  showZoom: boolean;
}>();

const emit = defineEmits(["update:zoomLevel", "first", "last", "next", "prev"]);
const zoomLevel = useVModel(props, "zoomLevel", emit);

// How much will you zoom in/out on each + or - click
const zoomButtonIncrease = 0.5;

const zoomIn = () => {
  if (zoomLevel.value + zoomButtonIncrease > 3) zoomLevel.value = 3;
  else zoomLevel.value += zoomButtonIncrease;
};
const zoomOut = () => {
  if (zoomLevel.value + zoomButtonIncrease < 1) zoomLevel.value = 1;
  else zoomLevel.value -= zoomButtonIncrease;
};
</script>

<style lang="scss" scoped>
.brochure-zoom-share {
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  gap: 1rem;
  background-color: $light-100;
  flex-wrap: nowrap;
  position: relative;
  z-index: 2;

  > .zoom-slider {
    flex: 1;
    max-width: 324px;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
}

.interactive:not(.disabled) {
  cursor: pointer;
}

// Input range styling generated by https://www.cssportal.com/style-input-range/
.zoom-input {
  height: 22px;
  appearance: none;
  width: 100%;
  max-width: 300px;
}
.zoom-input:focus {
  outline: none;
}
.zoom-input::-webkit-slider-runnable-track {
  width: 100%;
  height: 3px;
  background: $light-85;
  border-radius: 30px;
}
.zoom-input::-webkit-slider-thumb {
  border: 3px solid var(--primary);
  height: 13px;
  width: 13px;
  border-radius: 13px;
  appearance: none;
  margin-top: -5px;
}
.zoom-input:focus::-webkit-slider-runnable-track {
  background: $light-85;
}
.zoom-input::-moz-range-track {
  width: 100%;
  height: 3px;
  background: $light-85;
  border-radius: 30px;
}
.zoom-input::-moz-range-thumb {
  border: 3px solid var(--primary);
  height: 13px;
  width: 13px;
  border-radius: 13px;
}
.zoom-input::-ms-track {
  width: 100%;
  height: 3px;
  background: transparent;
  border-color: transparent;
  color: transparent;
}
.zoom-input::-ms-fill-lower {
  background: $light-85;
  border-radius: 60px;
}
.zoom-input::-ms-fill-upper {
  background: $light-85;
  border-radius: 60px;
}
.zoom-input::-ms-thumb {
  margin-top: 1px;
  border: 3px solid var(--primary);
  height: 13px;
  width: 13px;
  border-radius: 13px;
}
.zoom-input:focus::-ms-fill-lower {
  background: $light-85;
}
.zoom-input:focus::-ms-fill-upper {
  background: $light-85;
}
</style>
